// The library: the characters, places and props that shots are painted from. Each is one picture. Characters also
// have where their face goes on that picture (for previews) and a voice. Types only, shared by the server and browser.
import type { FaceSpots } from "./face";
import type { PaintStyle } from "./styles";
import type { Picture, PictureVersion } from "./episode";
import type { SpeechVoice } from "./speech";
import type { VoiceMode, VoiceSettings } from "./voice";

export const LIBRARY_VERSION = 3;

export type AssetKind = "character" | "background" | "prop";

// One picture ever made for an asset. The asset's own picture and face are the selected version's.
export type LookVersion = PictureVersion & { face?: FaceSpots };

export type Asset = {
  id: string;
  kind: AssetKind;
  name: string;
  description: string; // what its picture was painted from
  color: string; // UI colour: avatar ring, waveform
  picture: Picture; // characters are painted with a blank face, so the talking face can go on top
  face?: FaceSpots; // characters: where the eyes, brows and mouth go on the picture
  faceStyle?: string; // characters: which face sprites (src/library/faceSets.ts); none = the default
  versions?: LookVersion[]; // every picture made, oldest first; old assets have none (the current look is the only one)
  voiceMode?: VoiceMode; // characters only
  voice?: VoiceSettings;
  speech?: SpeechVoice;
  createdAt: number;
  updatedAt: number;
  usedAt?: number; // last time a picture was painted with it
};

export type Library = {
  version: number;
  assets: Asset[];
  styles?: PaintStyle[]; // drawing styles people wrote themselves (the app's own are in src/model/styles.ts)
};

export const findAsset = (lib: Library, id: string) => lib.assets.find(a => a.id === id);

// Newest first: whichever is later, when it was made or when a picture was last painted with it.
export const byRecent = (a: Asset, b: Asset) => Math.max(b.createdAt, b.usedAt ?? 0) - Math.max(a.createdAt, a.usedAt ?? 0);
